/**
 * API Client — KollegePlus Messenger
 *
 * Handles all HTTP communication with the backend.
 * Features:
 * - Automatic token refresh on 401
 * - Type-safe request/response
 * - Request queuing during token refresh
 */

// In Tauri desktop app, use absolute URL since the app runs on tauri://localhost
const isTauri = !!(window as unknown as Record<string, unknown>).__TAURI_INTERNALS__;
const BASE_URL = isTauri ? "https://app.kollegeplus.de/api/v1" : "/api/v1";

// ============================================================
// Token Management
// ============================================================

let accessToken: string | null = null;
let refreshToken: string | null = null;
// Single in-flight refresh promise — shared by ensureValidToken AND the 401 handler.
// Prevents N concurrent requests from each launching their own refresh, which would
// exhaust the single-use rotation token on the second attempt.
let activeRefresh: Promise<string> | null = null;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

// Cross-tab token sync: when one tab rotates the refresh token, broadcast so other
// tabs adopt the new tokens instead of trying to rotate the now-invalidated old one.
const tokenChannel: BroadcastChannel | null = (() => {
  try { return new BroadcastChannel("kp-token-sync"); } catch { return null; }
})();
tokenChannel?.addEventListener("message", (e: MessageEvent) => {
  if (e.data?.type === "tokens" && e.data.access && e.data.refresh) {
    accessToken = e.data.access as string;
    refreshToken = e.data.refresh as string;
    localStorage.setItem("refreshToken", e.data.refresh as string);
    mirrorAgentenTokens(e.data.access as string, e.data.refresh as string);
    scheduleProactiveRefresh(e.data.access as string);
  }
});

export function setTokens(access: string, refresh: string): void {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem("refreshToken", refresh);
  mirrorAgentenTokens(access, refresh);
  scheduleProactiveRefresh(access);
  tokenChannel?.postMessage({ type: "tokens", access, refresh });
}

// SSO bridge for the /agenten admin SPA (kollegeplus-agentenorchestrierung).
// That app reads its bearer token from localStorage under "kp.accessToken" /
// "kp.refreshToken". The messenger keeps its access token in memory only, so
// without this mirror the admin SPA would never see a session and would bounce
// to /login. Mirror on every token mutation; clear on logout.
function mirrorAgentenTokens(access: string, refresh: string): void {
  localStorage.setItem("kp.accessToken", access);
  localStorage.setItem("kp.refreshToken", refresh);
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("kp.accessToken");
  localStorage.removeItem("kp.refreshToken");
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

/**
 * Schedule a proactive token refresh before the access token expires.
 * Refreshes 90 seconds before expiry to avoid any race conditions.
 */
function scheduleProactiveRefresh(token: string): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return;
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    const exp = payload.exp as number | undefined;
    if (!exp) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const refreshInMs = Math.max((exp - nowSec - 90) * 1000, 5000); // At least 5s, 90s before expiry

    proactiveRefreshTimer = setTimeout(async () => {
      try {
        console.debug("[Auth] Proactive token refresh");
        await refreshAccessToken();
      } catch (err) {
        console.warn("[Auth] Proactive refresh failed:", err);
      }
    }, refreshInMs);

    console.debug(`[Auth] Proactive refresh scheduled in ${Math.round(refreshInMs / 1000)}s`);
  } catch {
    // Token parsing failed — don't schedule
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function loadStoredRefreshToken(): string | null {
  refreshToken = localStorage.getItem("refreshToken");
  return refreshToken;
}

/**
 * Check if the current access token is expired or about to expire.
 * If so, refresh it. Returns the valid token or null.
 */
export async function ensureValidToken(): Promise<string | null> {
  const token = getAccessToken();

  // No access token in memory — try to refresh using stored refresh token
  if (!token) {
    const stored = refreshToken || loadStoredRefreshToken();
    if (stored) {
      try {
        return await refreshAccessToken();
      } catch {
        return null;
      }
    }
    return null;
  }

  try {
    // Decode JWT payload (no verification, just expiry check)
    const parts = token.split(".");
    if (parts.length !== 3) return token;
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    const exp = payload.exp as number | undefined;
    if (!exp) return token;

    // Refresh if less than 60 seconds remaining
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp - nowSec < 60) {
      const newToken = await refreshAccessToken();
      return newToken;
    }

    return token;
  } catch {
    // On any error, return current token and let downstream handle it
    return token;
  }
}

// ============================================================
// Core Fetch Wrapper
// ============================================================

export class ApiError extends Error {
  status: number;
  code: string;
  data: Record<string, unknown> | null;

  constructor(status: number, code: string, message: string, data: Record<string, unknown> | null = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.data = data;
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  raw?: boolean; // Return raw Response instead of JSON
}

/**
 * Non-destructive self-heal for an `idk_fingerprint_mismatch` from /auth/refresh.
 *
 * The historic behaviour was to wipe ALL crypto on the first mismatch — but the
 * mismatch is usually a STALE cached fingerprint (kp_idk_fingerprint) that lags
 * the SPK the device actually published, not real key drift. This recomputes the
 * fingerprint from IDB truth and retries the refresh once with the corrected
 * value. Returns the new tokens on success, or null to let the caller fall
 * through to the destructive wipe (genuine drift: IDB itself disagrees with the
 * server, so re-login is the only recovery).
 *
 * NOTE: the rejecting /auth/refresh returns 401 BEFORE rotating the token, so the
 * same refresh token `rt` is still valid for the single retry here.
 */
async function _tryHealIdkFingerprintMismatch(
  rt: string,
): Promise<{ accessToken: string; refreshToken: string } | null> {
  const deviceId = localStorage.getItem("kollegeplus:deviceId") ?? undefined;
  if (!deviceId) return null;
  const sentFp = localStorage.getItem("kp_idk_fingerprint") ?? undefined;

  // Recompute the fingerprint from IndexedDB truth (the bundle we'd publish).
  const { getFirstDeviceKeys } = await import("./crypto-storage");
  const stored = await getFirstDeviceKeys();
  if (!stored || stored.deviceId !== deviceId) return null;
  const { deserializeDeviceKeys } = await import("../hooks/useCrypto-helpers");
  const { computeIdkFingerprint } = await import("./idk-fingerprint");
  const dk = deserializeDeviceKeys(stored);
  const idkFp = await computeIdkFingerprint(
    dk.identityKeyPair.curve25519.publicKey,
    dk.identityKeyPair.ed25519.publicKey,
    dk.signedPreKey.keyPair.publicKey,
  );
  localStorage.setItem("kp_idk_fingerprint", idkFp);

  // If IDB yields the SAME fingerprint we just sent, the cache was not stale —
  // the server genuinely holds different material. Nothing to heal; let the
  // caller wipe.
  if (idkFp === sentFp) return null;

  // Cache was stale: retry the refresh once with the corrected fingerprint.
  const retryRes = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt, deviceId, idkFingerprint: idkFp }),
  });
  if (!retryRes.ok) return null;
  return (await retryRes.json()) as { accessToken: string; refreshToken: string };
}

async function _doRefresh(): Promise<string> {
  const rt = refreshToken || loadStoredRefreshToken();
  if (!rt) throw new ApiError(401, "no_token", "No refresh token");

  // F19b: include device fingerprint so the server can fast-fail when local
  // key material has silently drifted from the published bundle. Both fields
  // must be present to trigger validation; older clients just send refreshToken.
  const deviceId = localStorage.getItem("kollegeplus:deviceId") ?? undefined;
  const idkFingerprint = localStorage.getItem("kp_idk_fingerprint") ?? undefined;
  const refreshBody: {
    refreshToken: string;
    deviceId?: string;
    idkFingerprint?: string;
  } = { refreshToken: rt };
  if (deviceId && idkFingerprint) {
    refreshBody.deviceId = deviceId;
    refreshBody.idkFingerprint = idkFingerprint;
  }

  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(refreshBody),
  });

  if (!res.ok) {
    // Only clear tokens on genuine auth failures (401/403). A 429 (rate-limited)
    // or 5xx (server error) should NOT destroy the session — the refresh token
    // is likely still valid, just temporarily unreachable.
    if (res.status === 401 || res.status === 403) {
      // F19b: distinguish identity-fingerprint mismatch from generic 401.
      // On mismatch the local IDB has diverged from the server-published
      // bundle — wipe local crypto state so the next login re-registers
      // fresh keys instead of replaying the stale ones forever.
      let errCode: string | undefined;
      try {
        const errBody = (await res.clone().json().catch(() => ({}))) as { error?: string };
        errCode = errBody?.error;
      } catch { /* ignore parse failure */ }

      if (errCode === "idk_fingerprint_mismatch") {
        // Before the destructive wipe, try a non-destructive self-heal. The common
        // cause is a STALE localStorage kp_idk_fingerprint that lags the SPK
        // actually published (re-register paths historically rewrote only
        // kp_keys_fingerprint). Recompute the fingerprint from IDB truth and retry
        // the refresh ONCE; only wipe when the server still rejects — i.e. the
        // local key material genuinely diverged, not just the cached fingerprint.
        const healed = await _tryHealIdkFingerprintMismatch(rt).catch(() => null);
        if (healed) {
          setTokens(healed.accessToken, healed.refreshToken);
          return healed.accessToken;
        }
        try {
          const { eraseAllCryptoData } = await import("./crypto-storage");
          await eraseAllCryptoData();
        } catch {
          // Best-effort: continue with localStorage cleanup even if IDB wipe fails.
        }
        try {
          localStorage.removeItem("kollegeplus:deviceId");
          localStorage.removeItem("kp_keys_fingerprint");
          localStorage.removeItem("kp_idk_fingerprint");
          localStorage.removeItem("kp_bot_session_reset_v2");
        } catch { /* non-critical */ }
        clearTokens();
        window.dispatchEvent(new CustomEvent("session-expired"));
        throw new ApiError(
          401,
          "idk_fingerprint_mismatch",
          "Lokaler Schlüsselzustand stimmt nicht mit Server überein. Bitte erneut anmelden.",
        );
      }

      clearTokens();
      // F7: Signal the UI so it can show a "please re-login" banner instead of
      // silently breaking requests. Fired only on definitive auth failure.
      window.dispatchEvent(new CustomEvent("session-expired"));
    }
    throw new ApiError(res.status, "refresh_failed", "Token refresh failed");
  }

  const data = (await res.json()) as { accessToken: string; refreshToken: string };
  setTokens(data.accessToken, data.refreshToken);
  return data.accessToken;
}

// Deduplicated refresh: N concurrent callers all await the same single fetch.
// Also handles cross-tab token broadcasts: another tab may have already rotated
// the token via BroadcastChannel, in which case `accessToken` is set by the
// listener before this function is called.
export function refreshAccessToken(): Promise<string> {
  if (!activeRefresh) {
    activeRefresh = _doRefresh().finally(() => { activeRefresh = null; });
  }
  return activeRefresh;
}

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, headers = {}, auth = true, raw = false } = options;

  const reqHeaders: Record<string, string> = {
    ...headers,
  };

  if (body && !headers["Content-Type"]) {
    reqHeaders["Content-Type"] = "application/json";
  }

  // Ensure we have a valid token before making authenticated requests
  if (auth && !accessToken) {
    const validToken = await ensureValidToken();
    if (validToken) accessToken = validToken;
  }

  if (auth && accessToken) {
    reqHeaders["Authorization"] = `Bearer ${accessToken}`;
  }

  // Multi-device E2EE: include device ID in all authenticated requests
  if (auth) {
    const deviceId = localStorage.getItem("kollegeplus:deviceId");
    if (deviceId) {
      reqHeaders["X-Device-Id"] = deviceId;
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: reqHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle 401 — try token refresh (concurrent callers share one in-flight refresh)
  if (res.status === 401 && auth && (refreshToken || loadStoredRefreshToken())) {
    const newToken = await refreshAccessToken();
    reqHeaders["Authorization"] = `Bearer ${newToken}`;
    const retryRes = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: reqHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (raw) return retryRes as unknown as T;
    if (!retryRes.ok) {
      const err = (await retryRes.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new ApiError(retryRes.status, err.error ?? "error", err.message ?? "Request failed");
    }
    return retryRes.json() as T;
  }

  if (raw) return res as unknown as T;

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string; message?: string };
    const apiErr = new ApiError(res.status, err.error ?? "error", err.message ?? "Request failed", err);

    // If TOTP setup is required, dispatch a global event so the app can redirect
    if (apiErr.code === "totp_setup_required") {
      window.dispatchEvent(new CustomEvent("totp-setup-required"));
    }

    throw apiErr;
  }

  return res.json() as T;
}

// ============================================================
// Convenience Methods
// ============================================================

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...options, method: "GET" }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "POST", body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PUT", body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PATCH", body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...options, method: "DELETE" }),

  /** Upload a file via multipart/form-data */
  upload: async <T>(path: string, fileOrFormData: File | FormData): Promise<T> => {
    const formData = fileOrFormData instanceof FormData
      ? fileOrFormData
      : (() => { const fd = new FormData(); fd.append("file", fileOrFormData); return fd; })();

    const headers: Record<string, string> = {};
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
    // Don't set Content-Type — browser sets it with boundary

    const res = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new ApiError(res.status, err.error ?? "error", err.message ?? "Upload failed");
    }

    return res.json() as T;
  },
};
